import { type ComponentPropsWithoutRef, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, type LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

interface BentoGridProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode
  className?: string
}

interface BentoCardProps extends ComponentPropsWithoutRef<"div"> {
  name: string
  className?: string
  background?: ReactNode
  Icon: LucideIcon
  description: ReactNode
  href?: string
  cta?: string
}

// Angepasst: Router-Link statt <a>, Lucide statt Radix-Icons, Farben aus den djiiga-Tokens.
const BentoGrid = ({ children, className, ...props }: BentoGridProps) => (
  <div className={cn("grid w-full auto-rows-[minmax(18rem,auto)] grid-cols-1 gap-4 md:grid-cols-3", className)} {...props}>
    {children}
  </div>
)

const BentoCard = ({ name, className, background, Icon, description, href, cta, ...props }: BentoCardProps) => (
  <div
    className={cn(
      "group relative flex flex-col justify-end overflow-hidden rounded-2xl border border-line bg-surface",
      "[box-shadow:0_-20px_80px_-20px_rgb(255_255_255/.06)_inset] transform-gpu",
      className
    )}
    {...props}
  >
    {background && <div className="pointer-events-none absolute inset-0">{background}</div>}
    <div className="relative z-10 flex flex-col gap-2 p-6 transition-transform duration-300 lg:group-hover:-translate-y-8">
      <span className="icon-chip mb-2 transition-transform duration-300 group-hover:scale-90">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <h3 className="text-xl font-semibold text-white">{name}</h3>
      <div className="max-w-lg leading-relaxed text-muted">{description}</div>
    </div>
    {href && cta && (
      <div className="relative z-10 px-6 pb-6 lg:absolute lg:inset-x-0 lg:bottom-0 lg:translate-y-6 lg:opacity-0 lg:transition-all lg:duration-300 lg:group-hover:translate-y-0 lg:group-hover:opacity-100">
        <Link to={href} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          {cta} <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
    )}
    <div className="pointer-events-none absolute inset-0 transition-colors duration-300 group-hover:bg-white/[.02]" />
  </div>
)

export { BentoCard, BentoGrid }
